<!-- PlanCatalogByName.vue -->
<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from "vue";
import { storeToRefs } from "pinia";
import { usePlanStore } from "@/stores/usePlanStore";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { useUserStore } from "@/stores/useUserStore";
import { useOnboardingStore } from "@/stores/useOnboardingStore";

const props = defineProps({
  // [PATCH] usageTypeFilter removido — não há mais distinção por tipo de uso
  showFreeContinueBtn: { type: Boolean, default: false },
});

const emit = defineEmits(["back", "select", "close", "continue-free"]);

/**
 * Emite:
 * - back
 * - select: { plan, mode }  // mode: 'pix_onetime' | 'card_onetime' | 'card_recurring' | 'downgrade_free'
 * - close
 */

const planStore = usePlanStore();
const subscriptionStore = useSubscriptionStore();
const userStore = useUserStore();
const onboardingStore = useOnboardingStore();

// ✅ store refs reativos
const { plans } = storeToRefs(planStore);
const { subscriptions } = storeToRefs(subscriptionStore);

const loading = ref(true);

// ✅ sempre reativo: fonte única para o chip "Atual"
const currentSubscriptionInfo = ref({ planId: null, paymentMethod: null });

// [PATCH] Substituição do dropdown "tipo de plano" por "contas gerenciadas".
// selectedManagedAccounts filtra planos pelo valor de maxManagedAccounts.
// O padrão é null, que representa "Todos" (sem filtro por maxManagedAccounts).
const selectedManagedAccounts = ref(null);
const selectedBillingCycle = ref("onetime");
const openTopDropdown = ref(null);
const openPaymentDropdown = ref(null);

// Cadastro/conta de escola: só planos dedicados a ela (nome começa com
// "Edu") entram no catálogo, e a vitrine ganha o tom "para escola". Três
// sinais contam como "contexto de escola":
// 1) a conta tem nome de instituição (users.institution_name, gravado pelo
//    cadastro "Sou uma escola") - o sinal definitivo, sobrevive a F5 e a
//    troca de plano;
// 2) onboardingStore.isEdu - setado por quem acabou de se cadastrar; só
//    dura a sessão de cadastro;
// 3) o plano atual do titular já começa com "Edu" - cobre as contas de
//    escola anteriores ao campo institution_name.
const isEduContext = computed(() => {
  // No contexto de membro, planName é o plano DA ESCOLA, e o catálogo é para a
  // assinatura da própria pessoa (ex.: aluno que abriu conta pessoal): o plano
  // da escola não pode empurrá-la para os planos Edu.
  const isManagedContext = userStore.currentUser?.loginContext === "managed";
  const currentPlanName = isManagedContext
    ? ""
    : (userStore.currentUser?.planName || "").trim().toLowerCase();
  return (
    !!userStore.currentUser?.institutionName ||
    onboardingStore.isEdu ||
    currentPlanName.startsWith("edu")
  );
});

function isVisiblePlan(p) {
  if (!p.isActive || !p.isPublic) return false;
  const isEduPlan = (p.name || "").trim().toLowerCase().startsWith("edu");
  // Partição nos dois sentidos: contexto de escola só vê planos "Edu *";
  // quem não é escola nunca vê planos "Edu *" misturados no catálogo geral.
  return isEduContext.value ? isEduPlan : !isEduPlan;
}

// [PATCH] Opções de contas gerenciadas derivadas dos planos carregados.
// Produz valores únicos e ordenados de maxManagedAccounts, com "Todos" no topo.
const managedAccountsOptions = computed(() => {
  const seen = new Set();
  for (const p of plans.value || []) {
    if (!isVisiblePlan(p)) continue;
    const val = p.maxManagedAccounts ?? 0;
    seen.add(val);
  }
  const specific = [...seen]
    .sort((a, b) => a - b)
    .map((v) => ({
      value: v,
      label: v === 0 ? "0 usuários" : `${v} usuário${v > 1 ? "s" : ""}`,
    }));
  // "Todos" é sempre a primeira opção e representa ausência de filtro (null)
  return [{ value: null, label: "Todos" }, ...specific];
});

// Mapa de normalização: valor do backend → label PT-BR
const allBillingCycleLabels = {
  onetime: "Pagamento único",
  monthly: "Mensal",
  yearly: "Anual",
};

// ✅ Label de cobrança exibido abaixo do preço no card
function cycleLabel(cycle) {
  const c = (cycle || "").toLowerCase();
  if (c === "onetime") return "pagamento único";
  if (c === "monthly") return "cobrado mensalmente";
  if (c === "yearly") return "cobrado anualmente";
  return "";
}

// ✅ Derivado: apenas os ciclos que existem para o filtro de contas gerenciadas selecionado
const billingCycleOptions = computed(() => {
  const seen = new Set();
  for (const p of plans.value || []) {
    if (!isVisiblePlan(p)) continue;
    // [PATCH] null = "Todos": não filtra por maxManagedAccounts
    if (
      selectedManagedAccounts.value !== null &&
      (p.maxManagedAccounts ?? 0) !== selectedManagedAccounts.value
    )
      continue;
    const cycle = (p.billingCycle || "").toLowerCase();
    if (cycle) seen.add(cycle);
  }
  // mantém a ordem canônica
  return ["onetime", "monthly", "yearly"]
    .filter((c) => seen.has(c))
    .map((c) => ({ value: c, label: allBillingCycleLabels[c] }));
});

// ✅ Quando o filtro de contas muda, garante que o ciclo selecionado ainda existe;
// caso contrário cai no primeiro disponível.
watch(billingCycleOptions, (opts) => {
  if (!opts.find((o) => o.value === selectedBillingCycle.value)) {
    selectedBillingCycle.value = opts[0]?.value ?? "onetime";
  }
});

const selectedManagedAccountsLabel = computed(() => {
  const found = managedAccountsOptions.value.find(
    (opt) => opt.value === selectedManagedAccounts.value
  );

  return found?.label ?? "Todos";
});

const selectedBillingCycleLabel = computed(() => {
  const found = billingCycleOptions.value.find(
    (opt) => opt.value === selectedBillingCycle.value
  );

  return (
    found?.label ??
    allBillingCycleLabels[selectedBillingCycle.value] ??
    "Selecione"
  );
});

const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

/** --------- Helpers de layout (imitando HomePage pricing) --------- */
// "Edu Plus"/"Edu Pro"/"Edu Premium" herdam a cor e o rótulo do plano geral
// de mesmo nome (ex.: "Edu Pro" fica com a cor e o texto de "Pro") — só o
// prefixo "Edu " é ignorado para essa comparação, os planos continuam
// distintos (linhas próprias na tabela plans).
function stripEduPrefix(name) {
  const n = (name || "").toLowerCase().trim();
  return n.startsWith("edu ") ? n.slice(4).trim() : n;
}

function planKeyFromName(name) {
  const n = stripEduPrefix(name);
  if (n === "free") return "free";
  if (n === "pro") return "pro";
  if (n === "plus") return "plus";
  if (n === "edu") return "edu";
  if (n === "premium") return "premium";
  return "other";
}

function planActionLabel(name) {
  const n = stripEduPrefix(name);

  // ✅ Free não é uma ação aqui — é só informativo
  if (n === "free") return "Incluído";
  if (n === "pro") return "Assinar Pro";
  if (n === "plus") return "Assinar Plus";
  if (n === "edu") return "Assinar Edu";
  return "Selecionar";
}

// ── Helpers de formatação de números ─────────────────────────────────────────
const CHARS_PER_PAGE = 2450;

/** Formata inteiro com separador de milhar pt-BR (ex.: 2.450.000) */
function fmtInt(n) {
  return new Intl.NumberFormat("pt-BR").format(n);
}

/** Converte caracteres → páginas arredondadas para baixo */
function toPages(chars) {
  return Math.floor((chars || 0) / CHARS_PER_PAGE);
}

// ✅ MUDANÇA: featuresForPlan agora recebe o objeto raw do plano e deriva
// todos os valores dinamicamente a partir dos campos do banco de dados:
//   maxTextCharacters, maxNoteCharacters, maxGroupUser, canExport,
//   maxManagedAccounts.
// O segundo argumento usageType foi removido — o compartilhamento é
// determinado exclusivamente por maxManagedAccounts.
function featuresForPlan(planObj) {
  if (!planObj) return [];

  const textChars = planObj.maxTextCharacters || 0;
  const noteChars = planObj.maxNoteCharacters || 0;
  const groupUsers = planObj.maxGroupUser || 0;
  const managed = planObj.maxManagedAccounts ?? 0;
  const canExport = planObj.canExport ?? false;

  const textPages = toPages(textChars);
  const notePages = toPages(noteChars);

  const features = [];

  // ── Texto ────────────────────────────────────────────────────────────────
  if (textPages > 0) {
    features.push(
      `${fmtInt(textPages)} página${
        textPages !== 1 ? "s" : ""
      } em texto (${fmtInt(textChars)} caracteres)`
    );
  } else if (textChars > 0) {
    features.push(`${fmtInt(textChars)} caracteres de texto`);
  }

  // ── Notas ────────────────────────────────────────────────────────────────
  if (notePages > 0) {
    features.push(
      `${fmtInt(notePages)} página${
        notePages !== 1 ? "s" : ""
      } em notas (${fmtInt(noteChars)} caracteres)`
    );
  } else if (noteChars > 0) {
    features.push(`${fmtInt(noteChars)} caracteres de notas`);
  }

  // ── Usuários em grupos ───────────────────────────────────────────────────
  if (groupUsers > 0) {
    features.push(
      `${fmtInt(groupUsers)} usuário${groupUsers !== 1 ? "s" : ""} pode${
        groupUsers !== 1 ? "m" : ""
      } ser vinculado${groupUsers !== 1 ? "s" : ""} a grupos`
    );
  }

  // ── Compartilhamento de limites (maxManagedAccounts) ─────────────────────
  const sharingLabel = getSharingLabel(managed);
  if (sharingLabel) features.push(sharingLabel);

  // ── Exportação PDF (condicional) ─────────────────────────────────────────
  if (canExport) {
    features.push("Exportação de notas em PDF");
  }

  return features;
}

// ── Lookup de compartilhamento baseado exclusivamente em maxManagedAccounts ───
function getSharingLabel(maxManagedAccounts) {
  if (maxManagedAccounts === 0) return "Titular não compartilha limites";
  if (maxManagedAccounts > 0)
    return `Titular pode compartilhar limites com até ${fmtInt(
      maxManagedAccounts
    )} usuário${maxManagedAccounts !== 1 ? "s" : ""}`;
  return null;
}

/** Normaliza ciclo e cria uma chave por nome do plano */
const grouped = computed(() => {
  // [PATCH] filtro por maxManagedAccounts em vez de usageType
  // activeManagedAccounts removido — selectedManagedAccounts.value usado diretamente
  const activeCycle = selectedBillingCycle.value;

  const activePlans = (plans.value || []).filter((p) => {
    if (!isVisiblePlan(p)) return false;
    // [PATCH] null = "Todos": ignora filtro por maxManagedAccounts
    if (
      selectedManagedAccounts.value !== null &&
      (p.maxManagedAccounts ?? 0) !== selectedManagedAccounts.value
    )
      return false;
    // filtra por ciclo de cobrança
    if ((p.billingCycle || "").toLowerCase() !== activeCycle) return false;
    return true;
  });

  const groups = new Map();

  for (const p of activePlans) {
    const name = p.name?.trim() || `Plano ${p.planId}`;
    const billing = (p.billingCycle || "").toLowerCase();

    if (!groups.has(name)) groups.set(name, { name, items: [] });
    groups.get(name).items.push({
      planId: p.planId,
      name,
      description: p.description,
      billingCycle: billing,
      price: Number(p.price || 0),
      raw: p,
    });
  }

  const out = [];
  for (const [, group] of groups) {
    const options = [];
    const groupNameLower = group.name.toLowerCase();
    const minItem =
      group.items.length > 0
        ? group.items.reduce((acc, cur) => (cur.price < acc.price ? cur : acc))
        : null;
    // Qualquer grupo de preço zero é tratado como "Free" (sem opção de
    // pagamento) — não só o plano chamado literalmente "Free". Isso cobre
    // também a variante gratuita de planos de escola, por exemplo.
    const isFreeTier = groupNameLower === "free" || (minItem?.price ?? 0) === 0;

    // 🔒 Grupos gratuitos não têm opções de pagamento neste fluxo
    if (!isFreeTier) {
      for (const item of group.items) {
        const priceFormatted = BRL.format(item.price);

        if (item.billingCycle === "onetime") {
          options.push({
            key: `${item.planId}-pix`,
            plan: item.raw,
            mode: "pix_onetime",
            label: `PIX — ${priceFormatted}`,
          });
          options.push({
            key: `${item.planId}-card`,
            plan: item.raw,
            mode: "card_onetime",
            label: `Cartão de crédito — ${priceFormatted}`,
          });
        } else if (item.billingCycle === "monthly") {
          options.push({
            key: `${item.planId}-rec-month`,
            plan: item.raw,
            mode: "card_recurring",
            label: `Cartão de crédito — ${priceFormatted}`,
          });
        } else if (item.billingCycle === "yearly") {
          options.push({
            key: `${item.planId}-rec-year`,
            plan: item.raw,
            mode: "card_recurring",
            label: `Cartão de crédito — ${priceFormatted}`,
          });
        }
      }
    }

    out.push({
      name: group.name,
      nameKey: isFreeTier ? "free" : planKeyFromName(group.name),
      description: group.items.find((i) => i.description)?.description || "",
      options,
      minPrice: minItem?.price ?? 0,
      minCycle: minItem?.billingCycle ?? "",
      // ✅ MUDANÇA: featuresForPlan recebe o objeto raw do plano para derivar
      // valores de caracteres/páginas/grupos diretamente do banco de dados.
      // Usa o primeiro item do grupo como fonte dos campos numéricos.
      features: featuresForPlan(group.items[0]?.raw ?? null),
    });
  }

  // ✅ Ordem ascendente de preço; empate desambiguado pelo nome
  out.sort((a, b) => {
    const diff = (a.minPrice ?? 0) - (b.minPrice ?? 0);
    if (diff !== 0) return diff;
    return (a.name || "").localeCompare(b.name || "");
  });

  return out;
});

// ✅ NOVO: quando grouped muda por troca de filtro, recalcula defaults
watch(grouped, () => {
  ensureDefaults();
});

const selectedByGroup = ref({}); // { [groupNameLower]: optionKey }

/** ----------------- ✅ NOVO: fonte reativa do "Atual" ----------------- */
function pickActiveSubscription(list) {
  const items = Array.isArray(list) ? list : [];
  const actives = items.filter(
    (s) => (s.status || "").toLowerCase() === "active"
  );
  if (actives.length === 0) return null;

  // tenta usar a mais recente se existir createdAt/updatedAt
  const best = actives.slice().sort((a, b) => {
    const ad = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const bd = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return bd - ad;
  })[0];

  return best || actives[0];
}

function refreshCurrentSubscriptionInfo() {
  const user = userStore.currentUser;

  // 1) Preferir assinatura active do store
  const active = pickActiveSubscription(subscriptions.value);
  if (active?.planId) {
    currentSubscriptionInfo.value = {
      planId: active.planId,
      paymentMethod: (active.paymentMethod || "").toLowerCase().trim(),
    };
    return;
  }

  // 2) Fallback por plano do usuário
  currentSubscriptionInfo.value = {
    planId: null,
    paymentMethod: (user?.planName || "").toLowerCase().trim(),
  };
}

/** ✅ Plano atual por coluna (chip "Atual") — NÃO pode marcar dois */
function isCurrentPlanGroup(grp) {
  const groupNameLower = (grp.name || "").toLowerCase().trim();

  // Regra 1: se temos planId do active, essa é a fonte única
  const currentPlanId = currentSubscriptionInfo.value.planId;
  if (currentPlanId && plans.value?.length) {
    const currentPlan = plans.value.find(
      (p) => String(p.planId) === String(currentPlanId)
    );

    const currentNameLower = (currentPlan?.name || "").toLowerCase().trim();
    return currentNameLower === groupNameLower;
  }

  // Regra 2: fallback por nome do user
  const userPlanName = (userStore.currentUser?.planName || "")
    .toLowerCase()
    .trim();
  return userPlanName !== "" && userPlanName === groupNameLower;
}

function isFeatured(grp) {
  return (grp.name || "").toLowerCase().trim() === "plus";
}

function isCurrentOption(opt) {
  if (!currentSubscriptionInfo.value.planId) return false;

  const isSamePlan =
    String(opt.plan.planId) === String(currentSubscriptionInfo.value.planId);

  if (!isSamePlan) return false;

  const activeMethod = currentSubscriptionInfo.value.paymentMethod; // pix|free|visa...
  const buttonMode = opt.mode;

  if (buttonMode === "downgrade_free") return activeMethod === "free";
  if (buttonMode === "pix_onetime") return activeMethod === "pix";

  if (buttonMode === "card_onetime" || buttonMode === "card_recurring") {
    return activeMethod !== "pix" && activeMethod !== "free";
  }

  return false;
}

const selectedOptionForGroup = (grp) => {
  const gKey = (grp.name || "").toLowerCase().trim();
  const key = selectedByGroup.value[gKey];
  return grp.options.find((o) => o.key === key) || null;
};

function preferredOptionKeyForCurrentPayment(grp) {
  const method = (currentSubscriptionInfo.value.paymentMethod || "")
    .toLowerCase()
    .trim();

  // pix
  if (method === "pix") {
    const pix = grp.options.find((o) => o.mode === "pix_onetime");
    if (pix) return pix.key;
  }

  // card (qualquer não pix e não free)
  if (method && method !== "pix" && method !== "free") {
    // preferir recorrente se existir
    const recurring = grp.options.find((o) => o.mode === "card_recurring");
    if (recurring) return recurring.key;

    const onetimeCard = grp.options.find((o) => o.mode === "card_onetime");
    if (onetimeCard) return onetimeCard.key;
  }

  return null;
}

function ensureDefaults() {
  // Começa com mapa limpo — remove chaves de grupos que não estão mais visíveis
  const map = {};

  for (const grp of grouped.value) {
    const gKey = grp.name.toLowerCase().trim();
    if (!grp.options || grp.options.length === 0) continue;

    // ✅ Se for o plano atual, selecionar o método atual (quando aplicável)
    if (isCurrentPlanGroup(grp)) {
      const preferred = preferredOptionKeyForCurrentPayment(grp);
      if (preferred) {
        map[gKey] = preferred;
        continue;
      }
    }

    // ✅ Sempre define: primeiro item não-atual, ou o primeiro disponível
    const firstNonCurrent = grp.options.find((o) => !isCurrentOption(o));
    map[gKey] = (firstNonCurrent || grp.options[0]).key;
  }

  selectedByGroup.value = map;
}

function requestClose(event = null) {
  // ✅ PATCH CIRÚRGICO: Proteção reforçada contra bubbling para AccountSummary/AccountSettingsModal
  if (event) {
    event.preventDefault();
    event.stopImmediatePropagation(); // Mais forte que stopPropagation
  }

  // Apenas emite para o UpgradePage (que já trata o caso modal vs página)
  emit("close");
}

function groupKey(grp) {
  return (grp?.name || "").toLowerCase().trim();
}

function toggleTopDropdown(dropdownName) {
  openPaymentDropdown.value = null;
  openTopDropdown.value =
    openTopDropdown.value === dropdownName ? null : dropdownName;
}

function closeTopDropdown() {
  openTopDropdown.value = null;
}

function selectManagedAccounts(value) {
  selectedManagedAccounts.value = value;
  closeTopDropdown();
}

function selectBillingCycle(value) {
  selectedBillingCycle.value = value;
  closeTopDropdown();
}

function closePaymentDropdown() {
  openPaymentDropdown.value = null;
}

function togglePaymentDropdown(grp) {
  const key = groupKey(grp);

  if (!key) return;

  openTopDropdown.value = null;
  openPaymentDropdown.value = openPaymentDropdown.value === key ? null : key;
}

function selectPaymentOption(grp, opt) {
  const key = groupKey(grp);

  if (!key || !opt?.key) return;

  selectedByGroup.value = {
    ...selectedByGroup.value,
    [key]: opt.key,
  };

  closePaymentDropdown();
}

function selectedPaymentLabel(grp) {
  const selected = selectedOptionForGroup(grp);

  return selected?.label ?? "Selecionar forma de pagamento";
}

function closeAllCatalogDropdowns() {
  openTopDropdown.value = null;
  openPaymentDropdown.value = null;
}

function handleOutsideCatalogDropdowns(event) {
  const target = event.target;

  if (!target || typeof target.closest !== "function") {
    closeAllCatalogDropdowns();
    return;
  }

  if (target.closest(".top-dropdown") || target.closest(".payment-dropdown")) {
    return;
  }

  closeAllCatalogDropdowns();
}

function handleEscKey(event) {
  if (event.key !== "Escape") return;

  if (openTopDropdown.value || openPaymentDropdown.value) {
    event.preventDefault();
    event.stopImmediatePropagation();
    closeAllCatalogDropdowns();
    return;
  }

  requestClose(event);
}

function handlePrimaryAction(grp) {
  // ✅ Grupos gratuitos: botão é informativo, não executa nada
  if (grp.nameKey === "free") return;

  const opt = selectedOptionForGroup(grp);
  if (!opt) return;

  if (isCurrentOption(opt)) return;

  emit("select", { plan: opt.plan, mode: opt.mode });
}

onMounted(async () => {
  loading.value = true;
  try {
    await planStore.fetchActivePlans();

    const user = userStore.currentUser;
    if (user?.userId) {
      await subscriptionStore.fetchSubscriptionsPaged(1, 100, user.userId);
    }

    // Cadastro/conta de escola: o catálogo já vem filtrado só para planos
    // "Edu *" (ver isVisiblePlan), então "Todos" já mostra só as opções
    // relevantes — não precisa mais pré-selecionar uma faixa de contas.

    refreshCurrentSubscriptionInfo();
    ensureDefaults();
  } catch (e) {
    console.error("Falha ao carregar planos:", e);
  } finally {
    loading.value = false;
  }

  document.addEventListener("keydown", handleEscKey);
  document.addEventListener("mousedown", handleOutsideCatalogDropdowns);
});

watch(
  () => [
    userStore.currentUser?.planName,
    userStore.currentUser?.planId,
    subscriptions.value?.length,
  ],
  () => {
    refreshCurrentSubscriptionInfo();
    ensureDefaults();
  }
);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("mousedown", handleOutsideCatalogDropdowns);
});
</script>

<template>
  <div class="plan-catalog">
    <div class="catalog-header">
      <div class="catalog-header-text">
        <span v-if="isEduContext" class="catalog-edu-badge"
          >Planos para Escola</span
        >
        <h2>
          {{
            isEduContext
              ? "Escolha o plano ideal para sua escola"
              : "Planos adaptados aos seus estudos"
          }}
        </h2>
        <p>
          {{
            isEduContext
              ? "Professores, coordenadores e alunos compartilham as contas do mesmo plano"
              : "Escolha a melhor opção para sua jornada de aprendizado"
          }}
        </p>
      </div>

      <!-- ✅ PATCH:
     Fecha apenas o catálogo. O .stop/.prevent aqui é evento DOM,
     portanto está correto. -->
      <button
        type="button"
        class="modal-close-btn"
        aria-label="Fechar"
        @click.stop.prevent="requestClose"
      >
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>

    <!-- dropdowns de filtro alinhados à esquerda -->
    <div ref="topFilterRoot" class="filter-bar">
      <div class="filter-group">
        <label class="filter-label" id="filter-managed-accounts-label">
          Número de usuários colaboradores:
        </label>

        <div class="top-dropdown top-dropdown-managed">
          <button
            type="button"
            class="top-dropdown-button"
            :class="{ open: openTopDropdown === 'managed' }"
            aria-haspopup="listbox"
            :aria-expanded="openTopDropdown === 'managed'"
            aria-labelledby="filter-managed-accounts-label"
            @click.stop="toggleTopDropdown('managed')"
          >
            <span class="top-dropdown-value">
              {{ selectedManagedAccountsLabel }}
            </span>
            <i
              class="fas fa-chevron-down top-dropdown-chevron"
              aria-hidden="true"
            ></i>
          </button>

          <div
            v-if="openTopDropdown === 'managed'"
            class="top-dropdown-menu"
            role="listbox"
            aria-labelledby="filter-managed-accounts-label"
          >
            <button
              v-for="opt in managedAccountsOptions"
              :key="`managed-${opt.value === null ? 'all' : opt.value}`"
              type="button"
              class="top-dropdown-option"
              :class="{ selected: opt.value === selectedManagedAccounts }"
              role="option"
              :aria-selected="opt.value === selectedManagedAccounts"
              @click.stop="selectManagedAccounts(opt.value)"
            >
              <span>{{ opt.label }}</span>
              <i
                v-if="opt.value === selectedManagedAccounts"
                class="fas fa-check top-dropdown-check"
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
      </div>

      <div class="filter-group">
        <label class="filter-label" id="filter-billing-cycle-label">
          Ciclo
        </label>

        <div class="top-dropdown top-dropdown-cycle">
          <button
            type="button"
            class="top-dropdown-button"
            :class="{ open: openTopDropdown === 'cycle' }"
            aria-haspopup="listbox"
            :aria-expanded="openTopDropdown === 'cycle'"
            aria-labelledby="filter-billing-cycle-label"
            @click.stop="toggleTopDropdown('cycle')"
          >
            <span class="top-dropdown-value">
              {{ selectedBillingCycleLabel }}
            </span>
            <i
              class="fas fa-chevron-down top-dropdown-chevron"
              aria-hidden="true"
            ></i>
          </button>

          <div
            v-if="openTopDropdown === 'cycle'"
            class="top-dropdown-menu"
            role="listbox"
            aria-labelledby="filter-billing-cycle-label"
          >
            <button
              v-for="opt in billingCycleOptions"
              :key="`cycle-${opt.value}`"
              type="button"
              class="top-dropdown-option"
              :class="{ selected: opt.value === selectedBillingCycle }"
              role="option"
              :aria-selected="opt.value === selectedBillingCycle"
              @click.stop="selectBillingCycle(opt.value)"
            >
              <span>{{ opt.label }}</span>
              <i
                v-if="opt.value === selectedBillingCycle"
                class="fas fa-check top-dropdown-check"
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="props.showFreeContinueBtn" class="free-continue-banner">
      <p>Prefere continuar no plano gratuito?</p>
      <button class="btn-free-continue" @click="emit('continue-free')">
        Continuar no Free →
      </button>
    </div>

    <div class="catalog-body">
      <div v-if="loading" class="loading-state">
        <svg
          class="animate-spin h-7 w-7 text-indigo-600 mx-auto mb-3"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="loading-text">Carregando planos disponíveis...</p>
      </div>

      <div v-else class="pricing-grid">
        <div v-if="grouped.length === 0" class="empty-state">
          Nenhum plano disponível para esta combinação de tipo e ciclo.
        </div>

        <article
          v-for="grp in grouped"
          :key="grp.name"
          class="price-card"
          :class="[`plan-${grp.nameKey}`, { featured: isFeatured(grp) }]"
        >
          <div v-if="isFeatured(grp)" class="popular-tag">Mais Popular</div>

          <div class="card-header">
            <h3>
              {{ grp.name }}
              <span v-if="isCurrentPlanGroup(grp)" class="current-label">
                Atual
              </span>
            </h3>
          </div>

          <p class="description">
            {{ grp.description || "—" }}
          </p>

          <div class="price">
            {{ BRL.format(grp.minPrice) }}
          </div>
          <p v-if="cycleLabel(grp.minCycle)" class="price-cycle">
            ({{ cycleLabel(grp.minCycle) }})
          </p>

          <ol v-if="grp.features?.length" class="features">
            <li v-for="(f, i) in grp.features" :key="i">
              <i class="fas fa-check"></i>
              <span>{{ f }}</span>
            </li>
          </ol>

          <div v-if="grp.options.length > 0" class="pay-block">
            <label class="pay-label" :id="`pay-label-${grp.nameKey}`">
              Forma de pagamento
            </label>

            <div class="payment-dropdown">
              <button
                type="button"
                class="payment-dropdown-button"
                :class="{ open: openPaymentDropdown === groupKey(grp) }"
                aria-haspopup="listbox"
                :aria-expanded="openPaymentDropdown === groupKey(grp)"
                :aria-labelledby="`pay-label-${grp.nameKey}`"
                @click.stop="togglePaymentDropdown(grp)"
              >
                <span class="payment-dropdown-value">
                  {{ selectedPaymentLabel(grp) }}
                </span>
                <i
                  class="fas fa-chevron-down payment-dropdown-chevron"
                  aria-hidden="true"
                ></i>
              </button>

              <div
                v-if="openPaymentDropdown === groupKey(grp)"
                class="payment-dropdown-menu"
                role="listbox"
                :aria-labelledby="`pay-label-${grp.nameKey}`"
              >
                <button
                  v-for="opt in grp.options"
                  :key="opt.key"
                  type="button"
                  class="payment-dropdown-option"
                  :class="{
 selected: opt.key === selectedByGroup[groupKey(grp)],
                  }"
                  role="option"
                  :aria-selected="opt.key === selectedByGroup[groupKey(grp)]"
                  @click.stop="selectPaymentOption(grp, opt)"
                >
                  <span>{{ opt.label }}</span>
                  <i
                    v-if="opt.key === selectedByGroup[groupKey(grp)]"
                    class="fas fa-check payment-dropdown-check"
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
            </div>
          </div>

          <div v-else class="free-note">
            Este é o plano gratuito. (Sem opções de pagamento neste fluxo.)
          </div>

          <button
            class="btn-plan"
            :class="`btn-${grp.nameKey}`"
            type="button"
            :disabled="
              grp.nameKey === 'free' ||
              (grp.options.length > 0 &&
                selectedOptionForGroup(grp) &&
                isCurrentOption(selectedOptionForGroup(grp)))
            "
            @click="handlePrimaryAction(grp)"
          >
            {{ grp.nameKey === "free" ? "Incluído" : planActionLabel(grp.name) }}
          </button>
        </article>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css");

.plan-catalog {
  height: 100%;
  font-family: "Poppins", sans-serif;
  background: var(--bg-main);
  border-radius: 14px;

  /* ✅ PATCH:
     O catálogo não deve rolar por inteiro.
     Cabeçalho, filtros e banner ficam fixos.
     A rolagem vertical será movida para .catalog-body,
     onde começam os cards dos planos. */
  overflow: hidden;

  padding: 16px 14px;
  border: 1px solid var(--border-color);
  position: relative;

  /* ✅ PATCH:
     Necessário para separar área fixa superior da área rolável dos cards. */
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.catalog-header {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 6px 6px 0;

  /* ✅ PATCH:
     Cabeçalho não participa da rolagem dos cards. */
  flex-shrink: 0;
}

.catalog-header-text {
  text-align: center;
}

.catalog-edu-badge {
  display: inline-block;
  background-color: #d8d0ff;
  color: var(--text-on-brand);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 3px 10px;
  border-radius: 999px;
  margin-bottom: 4px;
}

.catalog-header-text h2 {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.25;
}

.catalog-header-text p {
  /* ✅ PATCH compactação vertical:
     margem inferior reduzida (16px → 10px) para encurtar espaço
     entre o cabeçalho e a barra de filtros. */
  margin: 4px 0 10px;
  font-size: 0.9rem;
  color: var(--text-light);
}

/* O visual vem do .modal-close-btn canônico; a posição no canto é daqui
   (o cabeçalho centraliza o texto, então o X sai do fluxo). */
.modal-close-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  margin-top: 2px;
}

.filter-bar {
  display: flex;
  gap: 16px;
  justify-content: flex-start;
  flex-wrap: wrap;
  /* ✅ PATCH compactação vertical:
     margem inferior reduzida (20px → 12px) para encurtar o espaço
     entre os filtros e a área dos cards. */
  margin-bottom: 12px;
  align-items: center;

  /* ✅ PATCH:
     Filtros ficam fixos acima da área rolável dos cards. */
  flex-shrink: 0;
}

.filter-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: unset;
}

.filter-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-dark);
  white-space: nowrap;
  flex-shrink: 0;
}

.top-dropdown {
  position: relative;
  flex-shrink: 0;
}

.top-dropdown-managed {
  width: 142px;
}

.top-dropdown-cycle {
  width: 176px;
}

.top-dropdown-button {
  position: relative;
  width: 100%;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 32px 6px 12px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: var(--bg-content);
  color: var(--text-dark);
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease,
    background-color 0.15s ease;
}

.top-dropdown-button:hover {
  background: rgba(255, 255, 255, 0.92);
  border-color: rgba(189, 178, 255, 0.75);
}

.top-dropdown-button.open,
.top-dropdown-button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand);
  border-color: var(--brand-light);
}

.top-dropdown-value {
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.top-dropdown-chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0.55;
  font-size: 0.72rem;
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.top-dropdown-button.open .top-dropdown-chevron {
  transform: translateY(-50%) rotate(180deg);
  opacity: 0.8;
}

.top-dropdown-menu {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  z-index: 80;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  padding: 5px;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: var(--bg-content);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.14);
}

.top-dropdown-option {
  width: 100%;
  min-height: 30px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--text-dark);
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.top-dropdown-option:hover {
  background: rgba(189, 178, 255, 0.18);
}

.top-dropdown-option.selected {
  background: rgba(189, 178, 255, 0.32);
  color: #2a2457;
}

.top-dropdown-check {
  color: #6d5bd0;
  font-size: 0.72rem;
  flex-shrink: 0;
}

.catalog-body {
  /*
     O card destacado usa transform: scale(1.02).
     Quando ele é o primeiro card da linha, a escala cresce para a esquerda.
     Por isso precisamos de respiro horizontal real dentro da área rolável,
     senão overflow-x: hidden corta a borda esquerda.
  */
  padding: 20px 8px 0 8px;

  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  scrollbar-gutter: stable;
}

.loading-text {
  font-size: 0.88rem;
  color: var(--text-light);
}

.empty-state {
  padding: 0.75rem 0.25rem;
  text-align: center;
  font-size: 0.88rem;
  color: var(--text-light);
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
  align-items: stretch;

  /*
     O respiro lateral agora fica em .catalog-body.
     Aqui mantemos apenas o respiro inferior.
  */
  padding: 0 0 8px 0;
  overflow: visible;
}

.price-card {
  background: var(--bg-content);
  /* ✅ PATCH compactação vertical:
     padding vertical reduzido (18px → 14px). Padding horizontal
     mantido em 18px para preservar o respiro lateral do conteúdo. */
  padding: 14px 18px;
  border-radius: 18px;
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  transition: all 0.25s ease;
  position: relative;
}
.price-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08);
}

.price-card.featured {
  border: 2px solid var(--brand-light);
  transform: scale(1.02);
}

.popular-tag {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--brand-light);
  color: var(--text-on-brand);
  padding: 3px 12px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
}

.card-header h3 {
  font-size: 1.2rem;
  /* ✅ PATCH compactação vertical:
     margem inferior do título do card reduzida (6px → 2px). */
  margin: 0 0 2px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.current-label {
  background: #c1fba4;
  font-size: 0.68rem;
  padding: 2px 7px;
  border-radius: 6px;
  vertical-align: middle;
  font-weight: 700;
}

.description {
  color: var(--text-light);
  /* ✅ PATCH compactação vertical:
     line-height reduzido (1.45 → 1.35) e margem inferior mantida em 6px. */
  line-height: 1.35;
  margin: 0 0 6px 0;
  font-size: 0.9rem;
}

.price {
  font-size: 1.55rem;
  font-weight: 800;
  /* ✅ PATCH compactação vertical:
     margem superior do preço reduzida (12px → 6px). */
  margin: 6px 0 2px 0;
}

.price-cycle {
  font-size: 0.75rem;
  color: var(--text-light);
  /* ✅ PATCH compactação vertical:
     margem inferior do rótulo de ciclo reduzida (10px → 6px). */
  margin: 0 0 6px 0;
  font-weight: 500;
}

.features {
  list-style: none;
  padding: 0;
  /* ✅ PATCH compactação vertical:
     margem inferior da lista de features reduzida (12px → 8px). */
  margin: 0 0 8px 0;
  flex-grow: 1;
}

.features li {
  font-size: 0.88rem;
  /* ✅ PATCH compactação vertical:
     espaçamento entre itens reduzido (margin-bottom 8px → 4px).
     line-height explícito mantém legibilidade sem reduzir a fonte. */
  margin-bottom: 4px;
  line-height: 1.35;
  display: flex;
  align-items: center;
  gap: 8px;
}

.features i {
  color: var(--success);
}

.pay-block {
  margin: 0 0 6px 0;
}

.pay-label {
  display: block;
  font-weight: 800;
  font-size: 0.85rem;
  margin-bottom: 3px;
  color: var(--text-dark);
}

.payment-dropdown {
  position: relative;
  width: 100%;
}

.payment-dropdown-button {
  position: relative;
  width: 100%;
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 36px 7px 10px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.75);
  color: var(--text-dark);
  font-size: 0.72rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease,
    background-color 0.15s ease;
}

.payment-dropdown-button:hover {
  background: rgba(255, 255, 255, 0.92);
  border-color: rgba(189, 178, 255, 0.75);
}

.payment-dropdown-button.open,
.payment-dropdown-button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand);
  border-color: rgba(189, 178, 255, 0.65);
}

.payment-dropdown-value {
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.payment-dropdown-chevron {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0.7;
  font-size: 0.8rem;
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.payment-dropdown-button.open .payment-dropdown-chevron {
  transform: translateY(-50%) rotate(180deg);
  opacity: 0.85;
}

.payment-dropdown-menu {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  z-index: 90;
  width: 100%;
  max-height: 180px;
  overflow-y: auto;
  padding: 5px;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: var(--bg-content);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.14);
}

.payment-dropdown-option {
  width: 100%;
  min-height: 30px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--text-dark);
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.payment-dropdown-option:hover {
  background: rgba(189, 178, 255, 0.18);
}

.payment-dropdown-option.selected {
  background: rgba(189, 178, 255, 0.32);
  color: #2a2457;
}

.payment-dropdown-check {
  color: #6d5bd0;
  font-size: 0.7rem;
  flex-shrink: 0;
}

.free-note {
  /* ✅ PATCH compactação vertical:
     margens e padding internos reduzidos para ficar coerente
     com o adensamento dos demais blocos do card. */
  margin: 4px 0 6px 0;
  font-size: 0.86rem;
  color: var(--text-light);
  border: 1px dashed var(--border-color);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  padding: 8px 10px;
}

.btn-plan {
  /* ✅ PATCH compactação vertical:
     padding vertical do botão reduzido (10px → 8px). */
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s ease, box-shadow 0.15s ease;
  margin-top: auto;
  font-size: 0.92rem;
}

.btn-plan:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 16px rgba(0, 0, 0, 0.08);
}

.btn-plan:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.btn-pro {
  background: #ffd6a5;
  color: #6a4b1f;
}
.btn-plus {
  background: var(--brand-light);
  color: #2a2457;
}
.btn-free {
  background: #d1e8d1;
  color: #215921;
}
.btn-edu {
  background: #ffadad;
  color: #7a1f1f;
}
.btn-premium {
  background: #ffadad;
  color: #7a1f1f;
}
.btn-other {
  background: var(--neutral-200);
  color: var(--text-dark);
}

@media (max-width: 680px) {
  .price-card.featured {
    transform: none;
  }

  .plan-catalog {
    max-height: 90vh;
    margin-top: 6px;
    overflow: hidden;
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .free-continue-banner {
    flex-shrink: 0;
  }

  .filter-group {
    flex-direction: row;
    align-items: center;
  }

  .top-dropdown,
  .top-dropdown-managed,
  .top-dropdown-cycle,
  .payment-dropdown {
    width: 100%;
    min-width: 0;
  }
}
</style>